import React, { useState, useMemo } from "react";
import { ResponsiveModal } from "@wtc/ui";
import { BulkActionProgressModal } from "../BulkActionProgressModal";
import { useBulkEditSave } from "../hooks/useBulkEditSave";
import { renderCellValue } from "@/components/shared/UniversalCRMTable/utils";
import { BulkEditInfoBanner } from "./BulkEditInfoBanner";
import { BulkEditFilterBanner } from "./BulkEditFilterBanner";
import { BulkEditFieldInput } from "./BulkEditFieldInput";
import { BulkEditValuesPreview } from "./BulkEditValuesPreview";
import { BulkEditFooter } from "./BulkEditFooter";
import type {
  BaseEntity,
  SimpleFieldConfig,
  SimpleTableConfig,
  FieldValue,
} from "../../UniversalCRMTable/types";
import type { AuthenticatedUser } from "@/lib/types/unified";

export interface BulkEditModalProps<T extends BaseEntity> {
  isOpen: boolean;
  onClose: () => void;
  fieldId: string;
  fieldConfig: SimpleFieldConfig;
  selectedItems: T[];
  allItems: T[];
  config: SimpleTableConfig<T>;
  onBulkEdit: (fieldId: string, value: FieldValue, itemIds: string[]) => Promise<void>;
  appUser?: AuthenticatedUser;
  totalFilteredCount?: number;
  onBulkEditAllFiltered?: (
    fieldId: string,
    value: FieldValue,
    filterCriteria?: Record<string, unknown>
  ) => Promise<void>;
  filterCriteria?: Record<string, unknown>;
}

export function BulkEditModal<T extends BaseEntity>({
  isOpen,
  onClose,
  fieldId,
  fieldConfig,
  selectedItems,
  config: _config,
  onBulkEdit,
  totalFilteredCount,
  onBulkEditAllFiltered,
  filterCriteria,
}: BulkEditModalProps<T>) {
  const [editValue, setEditValue] = useState<string>("");
  const [applyToAllFiltered, setApplyToAllFiltered] = useState(false);

  React.useEffect(() => {
    if (isOpen) {
      setEditValue("");
      setApplyToAllFiltered(false);
    }
  }, [isOpen, fieldId]);

  const currentValues = useMemo(
    () =>
      selectedItems.map((item) => {
        const value = (item as any)[fieldId];
        const rendered = renderCellValue(value, fieldConfig, fieldId);
        return {
          id: item.id,
          value,
          // renderCellValue may return a React element (links, badges, relations);
          // fall back to the raw value for the plain-text preview in that case.
          displayValue:
            typeof rendered === "string" ? rendered : value == null ? "" : String(value),
        };
      }),
    [selectedItems, fieldId, fieldConfig]
  );

  const canApplyToAllFiltered = useMemo(
    () =>
      Boolean(
        totalFilteredCount &&
          totalFilteredCount > selectedItems.length &&
          onBulkEditAllFiltered &&
          filterCriteria
      ),
    [totalFilteredCount, selectedItems.length, onBulkEditAllFiltered, filterCriteria]
  );

  const {
    isLoading,
    isProcessing,
    showProgressModal,
    setShowProgressModal,
    progressItems,
    progressTitle,
    handleSave,
  } = useBulkEditSave({
    fieldId,
    fieldConfig,
    selectedItems,
    editValue,
    applyToAllFiltered,
    canApplyToAllFiltered,
    totalFilteredCount,
    filterCriteria,
    onBulkEdit,
  });

  const willUpdateCount =
    applyToAllFiltered && canApplyToAllFiltered ? totalFilteredCount ?? 0 : selectedItems.length;
  const updateButtonLabel =
    applyToAllFiltered && canApplyToAllFiltered ? "Update All" : "Update Selected";

  return (
    <ResponsiveModal
      open={isOpen}
      onOpenChange={onClose}
      title={`Bulk Edit ${fieldConfig.label}`}
      maxWidth="lg"
      side="bottom"
    >
      <div className="space-y-6">
        <BulkEditInfoBanner fieldLabel={fieldConfig.label} itemCount={selectedItems.length} />

        {canApplyToAllFiltered && (
          <BulkEditFilterBanner
            totalFilteredCount={totalFilteredCount!}
            selectedCount={selectedItems.length}
            applyToAllFiltered={applyToAllFiltered}
            onToggle={() => setApplyToAllFiltered(!applyToAllFiltered)}
          />
        )}

        <BulkEditFieldInput
          fieldId={fieldId}
          fieldConfig={fieldConfig}
          value={editValue}
          onValueChange={setEditValue}
        />

        <BulkEditValuesPreview currentValues={currentValues} />

        <BulkEditFooter
          willUpdateCount={willUpdateCount}
          isLoading={isLoading}
          showProgressModal={showProgressModal}
          updateButtonLabel={updateButtonLabel}
          onClose={onClose}
          onSave={handleSave}
          onCloseProgress={() => setShowProgressModal(false)}
        />
      </div>

      <BulkActionProgressModal
        isOpen={showProgressModal}
        onClose={() => setShowProgressModal(false)}
        title={progressTitle}
        totalItems={willUpdateCount}
        items={progressItems.map((item) => ({
          ...item,
          name: item.id,
          status: item.status as "pending" | "processing" | "success" | "error",
        }))}
        onRetryItem={(item) => {
          console.log("Retry item:", item);
        }}
        onRetryAllFailed={() => {
          console.log("Retry all failed items");
        }}
        isProcessing={isProcessing}
      />
    </ResponsiveModal>
  );
}
